// src/utils/dateFormatter.js

const SUPPORTED_DATE_FORMATS = new Set(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']);
let globalDateFormat = 'DD/MM/YYYY';

export const setGlobalDateFormat = (format) => {
  globalDateFormat = SUPPORTED_DATE_FORMATS.has(format) ? format : 'DD/MM/YYYY';
};

export const getGlobalDateFormat = () => globalDateFormat;

export const parseDateInput = (dateInput) => {
  if (!dateInput && dateInput !== 0) return null;

  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput;
  }

  if (typeof dateInput === 'number') {
    const date = new Date(dateInput);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof dateInput === 'string') {
    const value = dateInput.trim();
    if (!value) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(value)) {
      const normalized = value.replace(' ', 'T');
      const [datePart, timePart] = normalized.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number);
      const date = new Date(year, month - 1, day, hours, minutes, seconds);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

export const toInputDateValue = (dateInput = new Date()) => {
  const date = parseDateInput(dateInput);
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toInputDateTimeValue = (dateInput) => {
  const date = parseDateInput(dateInput);
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const formatDate = (dateInput, format = globalDateFormat) => {
  const date = parseDateInput(dateInput);
  if (!date) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  if (format === 'MM/DD/YYYY') return `${month}/${day}/${year}`;
  if (format === 'YYYY-MM-DD') return `${year}-${month}-${day}`;
  return `${day}/${month}/${year}`;
};

export const formatTime12Hour = (dateInput) => {
  const date = parseDateInput(dateInput);
  if (!date) return '';

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${ampm}`;
};

export const formatDateTime = (dateInput) => {
  const date = parseDateInput(dateInput);
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const datePart = globalDateFormat === 'MM/DD/YYYY'
    ? `${month}/${day}/${year}`
    : globalDateFormat === 'YYYY-MM-DD'
      ? `${year}-${month}-${day}`
      : `${day}/${month}/${year}`;
  return `${datePart} ${hours}:${minutes} ${ampm}`;
};
